/**
 * BookSeries.jsx — list of text-series (and chapter-grouped personal
 * books) accessible from the Library. Reached via the new "Book Series"
 * pill on the Library landing screen (iter 64).
 *
 * Data sources:
 *   • /api/text-series         — public text series (Aug 2026 system)
 *   • /api/library/my-books    — personal_books for the viewer (filter
 *                                to ones with 2+ chapters)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Loader2, Layers, ArrowLeft, ChevronRight, Search, User,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import WriterName from '../components/WriterName';
import { Sparkles, Coins } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SeriesCard = ({ series, onOpen }) => {
  // Feb 12 2026 bug fix — was reading `series.episodes.length` /
  // `series.chapter_count` / `series.chapters.length` and missing the
  // canonical `series.episode_count` that the /api/text-series payload
  // actually ships. Result: every public Book Series card showed
  // "0 episodes" even when the series had many. Add episode_count first
  // so the live count wins.
  const episodeCount = series.episode_count ||
    (series.episodes && series.episodes.length) ||
    series.chapter_count || (series.chapters && series.chapters.length) || 0;
  const author = series.author_username || series.author || series.author_user_id || '';
  const authorUserId = series.user_id || series.author_user_id || series.writer_user_id || null;
  const totalWords = series.total_word_count ||
    (series.chapters || []).reduce((a, c) => a + (c.word_count || 0), 0);
  const cover = series.cover_image || series.cover_image_url || series.cover_url;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group w-full text-left rounded-2xl border border-slate-700 hover:border-amber-400/60 bg-slate-800/60 hover:bg-slate-800/80 p-4 sm:p-5 transition-all flex gap-4 items-stretch"
      data-testid={`book-series-card-${series.series_id || series.book_id}`}
    >
      {/* Cover slot — same square treatment as AudioSeries / ContinueRail
          so the three list types feel symmetrical (Feb 12 2026 fix:
          user asked for "as designed and long as audio and ebook"). */}
      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden flex-shrink-0 bg-slate-900 border border-white/5 grid place-items-center">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <Layers className="w-8 h-8 text-amber-300/60" />
        )}
      </div>
      <div className="min-w-0 flex-1 flex flex-col">
        <p className="eyebrow text-amber-300 mb-1">
          <Layers className="w-3 h-3 inline mr-1" /> Book Series
        </p>
        <h3 className="font-display text-xl sm:text-2xl font-black text-white leading-tight truncate">
          {series.title || 'Untitled series'}
        </h3>
        {series.description && (
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{series.description}</p>
        )}
        <div className="mt-auto pt-2 flex items-center gap-3 text-[11px] text-slate-400 font-bold uppercase tracking-wider flex-wrap">
          {author && (
            <span className="inline-flex items-center gap-1">
              <User className="w-3 h-3" />
              <WriterName userId={authorUserId} name={author} stopPropagation className="text-slate-300" />
            </span>
          )}
          <span className="tabular-nums">
            {episodeCount} {episodeCount === 1 ? 'episode' : 'episodes'}
          </span>
          {totalWords > 0 && (
            <span className="tabular-nums">
              {totalWords.toLocaleString()} words
            </span>
          )}
          {/* Feb 12 2026 user spec — writer XP earned badge, visible to
              owner / King / High King only (backend strips the fields
              for reader-class viewers). Coins badge sits beside it so
              the scribe sees the full payout at a glance. */}
          {typeof series.writer_xp_earned === 'number' && series.writer_xp_earned > 0 && (
            <span
              className="inline-flex items-center gap-1 text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5 tabular-nums"
              data-testid={`book-series-xp-${series.series_id}`}
              title="Scribe XP earned across this series"
            >
              <Sparkles className="w-3 h-3" />
              +{series.writer_xp_earned} XP
            </span>
          )}
          {typeof series.writer_coins_earned === 'number' && series.writer_coins_earned > 0 && (
            <span
              className="inline-flex items-center gap-1 text-emerald-200 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5 tabular-nums"
              data-testid={`book-series-coins-${series.series_id}`}
              title="Writer coins earned across this series"
            >
              <Coins className="w-3 h-3" />
              +{series.writer_coins_earned}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0 self-center group-hover:text-amber-300 transition-colors" />
    </button>
  );
};

const BookSeries = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [myBooks, setMyBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Fix Feb 12 2026: page was landing scrolled to the bottom because
    // the rest of the SPA preserves scroll position across navigations.
    // Force a top-of-page render the moment the user opens this page.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      authFetch(`${BACKEND_URL}/api/text-series`).then((r) => r.ok ? r.json() : { series: [] }),
      authFetch(`${BACKEND_URL}/api/library/my-books`).then((r) => r.ok ? r.json() : { books: [] })
        .catch(() => ({ books: [] })),
    ]).then(([textRes, myRes]) => {
      if (!alive) return;
      const txt = textRes.status === 'fulfilled' ? (textRes.value.series || []) : [];
      const mine = myRes.status === 'fulfilled'
        ? ((myRes.value.books || []).filter((b) => (b.chapters || []).length >= 2))
        : [];
      setSeries(txt);
      setMyBooks(mine);
      setLoading(false);
    }).catch((e) => {
      if (!alive) return;
      setError(typeof e?.message === 'string' ? e.message : 'Failed to load series');
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const matches = (s) => {
    if (!query) return true;
    const hay = `${s.title || ''} ${s.author_username || s.author || ''}`.toLowerCase();
    return hay.includes(query.toLowerCase());
  };

  const open = (s) => {
    if (s.series_id) navigate(`/text-series/${s.series_id}`);
    else if (s.book_id) navigate(`/my-library/book/${s.book_id}`);
  };

  const filteredSeries = series.filter(matches);
  const filteredMine = myBooks.filter(matches);
  const empty = !loading && filteredSeries.length === 0 && filteredMine.length === 0;

  return (
    <main className="sharp-bg max-w-3xl mx-auto px-4 py-6 pb-32 min-h-screen" data-testid="book-series-page">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => navigate('/library')}
          className="text-white/60 hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
          data-testid="book-series-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Library
        </button>
        <p className="eyebrow text-amber-300">Book series</p>
        <span className="w-12" />
      </div>

      <header className="mb-5">
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-[1.02]">
          Series<span className="text-amber-300">.</span>
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Multi-part books — text series with episodes, plus your own chaptered drafts.
        </p>
      </header>

      {/* Academic Library entry — sits *under* Book Series per Feb 12
          2026 user spec. Tap to enter the Projects / Publications hub. */}
      <button
        type="button"
        onClick={() => navigate('/academic-library')}
        className="group w-full text-left mb-5 rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 via-amber-500/8 to-fuchsia-500/10 hover:from-amber-500/25 hover:to-fuchsia-500/15 p-4 sm:p-5 transition-colors flex items-center gap-4"
        data-testid="book-series-academic-link"
      >
        <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-400/30 grid place-items-center text-2xl flex-shrink-0">
          🎓
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
            Academic Library
          </p>
          <p className="text-base font-bold text-white mt-0.5">Projects · Publications</p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Scribe-authored study material — title, author, year, price.
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-amber-300 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </button>

      <div className="relative mb-5">
        <Search className="w-4 h-4 text-white/35 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search series or author…"
          className="w-full bg-[#12131A] border border-white/10 rounded-full text-white text-sm pl-11 pr-4 py-3 placeholder:text-white/30 focus:outline-none focus:border-amber-400/50"
          data-testid="book-series-search"
        />
      </div>

      {loading && (
        <div className="text-center text-slate-400 text-sm py-8">
          <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading series…
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="book-series-error">
          {error}
        </div>
      )}
      {empty && (
        <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-8 text-center text-sm text-slate-400" data-testid="book-series-empty">
          No series yet. Writers and admins can launch one from the Upload hub.
        </div>
      )}

      {filteredSeries.length > 0 && (
        <section className="mb-7" data-testid="book-series-public">
          <p className="eyebrow text-amber-300 mb-3">Catalog</p>
          <div className="space-y-2">
            {filteredSeries.map((s) => (
              <SeriesCard key={s.series_id} series={s} onOpen={() => open(s)} />
            ))}
          </div>
        </section>
      )}

      {filteredMine.length > 0 && (
        <section data-testid="book-series-personal">
          <p className="eyebrow text-fuchsia-300 mb-3 inline-flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> Your chaptered books
          </p>
          <div className="space-y-2">
            {filteredMine.map((b) => (
              <SeriesCard
                key={b.book_id}
                series={{
                  ...b,
                  chapter_count: (b.chapters || []).length,
                  author_username: b.author_username || 'You',
                }}
                onOpen={() => open(b)}
              />
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default BookSeries;
